/**
 * DNASummaryBar - Strategy DNA Summary Statistics Bar
 *
 * Displays overall bounce/break/fakeout rates and MFE/MAE metrics
 * in a compact 2-row format matching existing QuantX styling.
 *
 * Height: 48px (2 rows of data)
 */

import React, { memo } from 'react';
import { Box, Typography, Tooltip, IconButton, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Get color for bounce rate
 */
const getBounceColor = (rate) => {
    if (!rate && rate !== 0) return '#6b7280';
    if (rate >= 60) return '#22c55e';  // Green - good bounce rate
    if (rate >= 40) return '#fbbf24';  // Amber - moderate
    return '#ef4444';                   // Red - poor
};

/**
 * Get color for fakeout rate (inverse - lower is better)
 */
const getFakeoutColor = (rate) => {
    if (!rate && rate !== 0) return '#6b7280';
    if (rate <= 15) return '#22c55e';  // Green - low fakeout
    if (rate <= 30) return '#fbbf24';  // Amber - moderate risk
    return '#ef4444';                   // Red - high fakeout risk
};

/**
 * Mini percentage chip
 */
const RateChip = memo(({ label, value, getColor }) => {
    const color = getColor(value);
    const displayValue = value != null ? `${value.toFixed(1)}%` : '--';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace', fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '11px', color, fontFamily: 'monospace', fontWeight: 700 }}>
                {displayValue}
            </Typography>
        </Box>
    );
});

RateChip.displayName = 'RateChip';

/**
 * MFE/MAE metric display
 */
const MetricChip = memo(({ label, value, suffix = 'p' }) => {
    if (value == null) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>
                    --
                </Typography>
            </Box>
        );
    }

    const isPositive = value >= 0;
    const color = isPositive ? '#22c55e' : '#ef4444';
    const prefix = isPositive ? '+' : '';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '10px', color, fontFamily: 'monospace', fontWeight: 700 }}>
                {prefix}{value.toFixed(1)}{suffix}
            </Typography>
        </Box>
    );
});

MetricChip.displayName = 'MetricChip';

/**
 * DNASummaryBar Component
 */
const DNASummaryBar = memo(({
    summary,
    loading = false,
    onRefresh,
    totalEvents
}) => {
    // Extract summary stats with fallbacks
    const bounceRate = summary?.bounceRate;
    const breakRate = summary?.breakRate;
    const fakeoutRate = summary?.fakeoutRate;
    const avgMfePips = summary?.avgMfePips;
    const avgMaePips = summary?.avgMaePips;
    const events = totalEvents || summary?.totalEvents || 0;

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '2px', overflow: 'hidden' }}>
            {/* Row 1: Title + Main Rates */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '5px 8px',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.3)'
                }}
            >
                {/* Left: Title + Event Count */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#dc2626',
                            fontFamily: 'monospace',
                            letterSpacing: '0.5px'
                        }}
                    >
                        STRATEGY DNA
                    </Typography>
                    <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace' }}>
                        {events} events
                    </Typography>
                </Box>

                {/* Center: Rate Chips */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <RateChip label="BOUNCE" value={bounceRate} getColor={getBounceColor} />
                    <Box sx={{ width: '1px', height: '12px', backgroundColor: 'rgba(55, 65, 81, 0.5)' }} />
                    <RateChip label="BREAK" value={breakRate} getColor={() => '#9ca3af'} />
                    <Box sx={{ width: '1px', height: '12px', backgroundColor: 'rgba(55, 65, 81, 0.5)' }} />
                    <RateChip label="FAKE" value={fakeoutRate} getColor={getFakeoutColor} />
                </Box>

                {/* Right: Refresh Button */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {onRefresh && (
                        <Tooltip title="Refresh DNA data">
                            <IconButton
                                onClick={onRefresh}
                                disabled={loading}
                                size="small"
                                sx={{
                                    padding: '2px',
                                    color: '#6b7280',
                                    '&:hover': { color: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.1)' }
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={12} sx={{ color: '#dc2626' }} />
                                ) : (
                                    <RefreshIcon sx={{ fontSize: 14 }} />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Row 2: MFE/MAE Metrics */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '4px 8px',
                    gap: 3,
                    backgroundColor: 'rgba(55, 65, 81, 0.05)'
                }}
            >
                <MetricChip label="AVG MFE" value={avgMfePips} suffix=" pips" />
                <MetricChip label="AVG MAE" value={avgMaePips} suffix=" pips" />

                {/* Additional context if available */}
                {summary?.lastEventAt && (
                    <Box sx={{ marginLeft: 'auto' }}>
                        <Typography sx={{ fontSize: '8px', color: '#6b7280', fontFamily: 'monospace' }}>
                            Last: {new Date(summary.lastEventAt).toLocaleDateString()}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
});

DNASummaryBar.displayName = 'DNASummaryBar';

export default DNASummaryBar;
